/**
 * Category tree helpers shared by shop UI breadcrumbs and SEO JSON-LD.
 * Mirrors frontend categoryHeader.js (keep logic in sync).
 */

const findCategoryById = (categories, targetId) => {
  if (!Array.isArray(categories) || targetId == null) {
    return null;
  }
  for (const category of categories) {
    if (category.id === targetId) {
      return category;
    }
    if (category.children) {
      const found = findCategoryById(category.children, targetId);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const findParentCategory = (categories, targetCategory) => {
  if (!categories || !targetCategory) {
    return null;
  }

  for (const category of categories) {
    if (
      category.children &&
      category.children.some((child) => child.id === targetCategory.id)
    ) {
      return category;
    }
    if (category.children) {
      const found = findParentCategory(category.children, targetCategory);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const getParentChain = (categories, targetId) => {
  const chain = [];

  const findParents = (cats, id, parentChain) => {
    if (!Array.isArray(cats)) {
      return false;
    }

    for (const cat of cats) {
      if (cat.id === id) {
        parentChain.unshift(cat);
        return true;
      }
      if (cat.children) {
        if (findParents(cat.children, id, parentChain)) {
          parentChain.unshift(cat);
          return true;
        }
      }
    }
    return false;
  };

  findParents(categories, targetId, chain);
  return chain;
};

const filterRedundantCategories = (categories, categoryIds) => {
  if (!categoryIds || categoryIds.length === 0) {
    return [];
  }

  const filteredIds = [];

  for (const id of categoryIds) {
    const category = findCategoryById(categories, id);
    if (!category) {
      continue;
    }

    const parentCategory = findParentCategory(categories, category);
    const isParentInList =
      parentCategory && categoryIds.includes(parentCategory.id);

    if (!isParentInList) {
      filteredIds.push(id);
    }
  }

  return filteredIds;
};

const categoryLabel = (category, language = "nl") => {
  if (!category?.category) {
    return undefined;
  }
  if (typeof category.category === "string") {
    return category.category;
  }
  return (
    category.category[language] ||
    category.category.nl ||
    category.category.en ||
    category.category.fr ||
    undefined
  );
};

/**
 * Build breadcrumb name/url items for the primary (first) category chain.
 * Category URLs use `/shop/c/{slug}/...` (see categoryPaths.js).
 *
 * @returns {Array<{ name: string, url?: string }>}
 */
const buildCategoryBreadcrumbItems = ({
  categories,
  categoryIds,
  language = "nl",
  shopBaseUrl,
  productCanonicalUrl,
  productName,
} = {}) => {
  const {
    buildCategoryShopPath,
    CATEGORY_URL_LANG,
  } = require("./categoryPaths");

  const items = [];
  if (shopBaseUrl) {
    items.push({ name: "Shop", url: shopBaseUrl });
  }

  const ids = Array.isArray(categoryIds)
    ? categoryIds
    : categoryIds != null
      ? [categoryIds]
      : [];

  if (Array.isArray(categories) && ids.length > 0) {
    const filteredIds = filterRedundantCategories(categories, ids);
    const chain = filteredIds.length
      ? getParentChain(categories, filteredIds[0])
      : [];

    for (const category of chain) {
      const name = categoryLabel(category, language);
      if (!name) {
        continue;
      }
      const categoryUrl = shopBaseUrl
        ? buildCategoryShopPath(
            shopBaseUrl,
            category,
            categories,
            CATEGORY_URL_LANG
          )
        : undefined;
      items.push({
        name,
        ...(categoryUrl ? { url: categoryUrl } : {}),
      });
    }
  }

  if (productCanonicalUrl && productName) {
    items.push({ name: productName, url: productCanonicalUrl });
  }

  return items;
};

module.exports = {
  buildCategoryBreadcrumbItems,
  categoryLabel,
  filterRedundantCategories,
  findCategoryById,
  getParentChain,
};
